import { useMemo, useState } from "react";
import { Table } from "../../table/Table";
import { Flex, HStack, VStack } from "../../../../styled-system/jsx";
import { RT8Pagination } from "../../table/Pagination";
import { resetTable, selectedIdsToSelectionState } from "../../table/utils";
import { SearchInput } from "../../fields/base/TextField";
import { createColumnBuilder } from "../../table/columnBuilder";
import { OrgCell } from "../../../modules/organization/OrgCell";
import { useClientTable, type AppColumnDef } from "../../table/tableFeatures";
import { type Organization } from "../../../zodios/common/orgSchema";
import { Button } from "../../core/basic/Button";
import { MdRotateLeft } from "react-icons/md";
import { RT8ColumnSelector } from "../../core/blocks/MetricSelector";
import { usePersistedTableState } from "../../table/usePersistedTableState";
import { Switch } from "../../fields/base/Switch";
import { useAppSelector } from "../../../redux/hooks";
import Loading from "../../core/blocks/Loading";
import { subject } from "@casl/ability";
import { useAbility } from "../../../config/casl/casl";
import { useTranslation } from "react-i18next";

const columnHelper = createColumnBuilder<Organization>();

interface Props {
  onChange: (selectedOrgIds: number[]) => void;
  selectedOrgIds: number[];
  requireCurrentOrg?: boolean;
  displaySelectOrgFilters?: boolean;
  additionalColumns?: AppColumnDef<Organization>[];
  orgs: Organization[];
  isLoading?: boolean;
  enableHiddenOrgsFilter?: boolean;
}

export const buildOrgSelectionBaseColumns = ({
  organizationLabel,
  organizationIdLabel,
}: {
  organizationLabel: string;
  organizationIdLabel: string;
}) => {
  return [
    columnHelper.accessor("name", {
      header: organizationLabel,
      enableHiding: false,
      enableSorting: true,
      cell: (props) => {
        return <OrgCell orgData={props.row.original} changeOrgOnClick={false} />;
      },
      minSize: 350,
    }),
    columnHelper.accessor("id", {
      header: organizationIdLabel,
      enableHiding: false,
      enableSorting: true,
    }),
  ];
};

const OrgSelectionTable = ({
  onChange,
  selectedOrgIds,
  requireCurrentOrg = false,
  displaySelectOrgFilters = true,
  additionalColumns,
  orgs,
  isLoading = false,
  enableHiddenOrgsFilter = false,
}: Props) => {
  const { t } = useTranslation(["admin", "common"]);
  const [filterSelectedOrgs, setFilterSelectedOrgs] = useState(false);
  const [showHiddenOrgs, setShowHiddenOrgs] = useState(false);
  const user = useAppSelector((state) => state.user);
  const ability = useAbility();

  const showHiddenOrgsSwitch = enableHiddenOrgsFilter && orgs.some((org) => !org.is_visible);

  const filteredOrgs = useMemo(() => {
    let visibleOrgs = orgs;

    if (showHiddenOrgsSwitch && !showHiddenOrgs) {
      visibleOrgs = visibleOrgs.filter((org) => org.is_visible || selectedOrgIds.includes(org.id));
    }

    if (filterSelectedOrgs) {
      visibleOrgs = visibleOrgs.filter((org) => selectedOrgIds.includes(org.id));
    }

    return visibleOrgs;
  }, [orgs, filterSelectedOrgs, selectedOrgIds, showHiddenOrgsSwitch, showHiddenOrgs]);

  const baseColumns = useMemo(() => {
    const cols = [
      columnHelper.accessor("name", {
        header: t(($) => $.admin["Organization"]),
        enableHiding: false,
        enableSorting: true,
        cell: (props) => {
          return <OrgCell orgData={props.row.original} changeOrgOnClick={false} />;
        },
        minSize: 350,
      }),
      ...(user.is_staff ?
        [
          columnHelper.accessor("id", {
            header: t(($) => $.admin["Organization ID"]),
            enableHiding: false,
            enableSorting: true,
          }),
        ]
      : []),
    ];

    return cols;
  }, [user.is_staff, t]);

  const columns = useMemo(() => [...baseColumns, ...(additionalColumns ?? [])], [baseColumns, additionalColumns]);

  const { setters, state, initialState } = usePersistedTableState({
    persistKey: "orgSelectionTable",
    initialState: {
      pagination: {
        pageSize: 5,
        pageIndex: 0,
      },
      sorting: [{ id: "id", desc: false }],
      rowSelection: selectedIdsToSelectionState(selectedOrgIds),
    },
  });

  const table = useClientTable({
    data: filteredOrgs,
    columns,
    initialState: initialState,
    getRowId: (row) => row.id.toString(),
    enableGlobalFilter: true,
    enableRowSelection: (row) =>
      ability.can("update", subject("User", { org_id: row.original.id })) &&
      (!requireCurrentOrg || row.original.id !== Number(user.organization_id)),
    state: {
      ...state,
      rowSelection: selectedIdsToSelectionState(selectedOrgIds),
    },
    ...setters,
    onRowSelectionChange: (updater) => {
      const rowSelectionState = typeof updater === "function" ? updater(table.state.rowSelection) : updater;
      const updatedIds = Object.keys(rowSelectionState);
      onChange(updatedIds.map((id) => parseInt(id)));
    },
  });

  return (
    <VStack>
      {isLoading ?
        <Loading fullPage={false} height={"200px"} />
      : <>
          <HStack gap={"large"} justifyContent={"space-between"} width={"100%"}>
            <VStack>
              <HStack>
                <Button
                  variant={"contentOnly"}
                  onPress={() => {
                    resetTable(table);
                  }}
                  fontSize={"xxlarge"}
                >
                  <MdRotateLeft />
                </Button>
                <RT8ColumnSelector table={table} />
                <SearchInput
                  aria-label={t(($) => $.common.actions["Search"])}
                  isExpandable
                  css={{ flex: "0 1 350px" }}
                  value={table.state.globalFilter}
                  onChange={table.setGlobalFilter}
                />
              </HStack>
            </VStack>
            {(displaySelectOrgFilters || showHiddenOrgsSwitch) && (
              <Flex gap="medium" whiteSpace="nowrap">
                {displaySelectOrgFilters && (
                  <Switch isSelected={filterSelectedOrgs} onChange={(isSelected) => setFilterSelectedOrgs(isSelected)}>
                    {t(($) => $.admin["Show Selected Orgs"])}
                  </Switch>
                )}
                {showHiddenOrgsSwitch && (
                  <Switch isSelected={showHiddenOrgs} onChange={(isSelected) => setShowHiddenOrgs(isSelected)}>
                    {t(($) => $.admin["Show Hidden Orgs"])}
                  </Switch>
                )}
              </Flex>
            )}
            <RT8Pagination table={table} canSetPageSize={true} />
          </HStack>
          <Table table={table} />
        </>
      }
    </VStack>
  );
};

export default OrgSelectionTable;
